import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  PlayArrow,
  Pause,
  Replay,
  CheckCircle,
  CheckCircleOutline,
  DoneAll,
  AccessTime,
  Star,
} from "@mui/icons-material";

import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import { BookmarkBorder, Bookmark } from "@mui/icons-material";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import { RED, RED_LIGHT, GREEN, GREEN_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";
import { fmtIng, fmtPantry, scaleIng } from "utils/ingredientFormatting";
import { RECIPE_DIET_STYLE, recipeDietLabel } from "utils/dietPreference";
import { CATALOG_DEFAULT_SERVINGS } from "utils/servings";
import { useUnitSystem } from "context/UnitSystemContext";
import UnitToggle from "components/UnitToggle";
import RewardMoment from "./RewardMoment";
import TechniqueChips, { isCollapsed } from "./TechniqueChips";
import { MEAL_TYPE_BADGE as MEAL_TYPE_LABELS } from "utils/mealTypes";
import { passiveWaitLabel } from "utils/passiveWait";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

// Staples arrive either as a bare name (older payloads) or as a full row.
// Anything with a quantity goes through the shared formatter so a staple
// agrees with its quantity the same way an ingredient does.
function fmtPantryItem(item, metric) {
  if (typeof item === "string") return item;
  if (!item.quantity) return item.name;
  return fmtPantry(item, metric) || item.name;
}

Toggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

function Toggle({ checked, onChange, label }) {
  return (
    <Box
      onClick={onChange}
      sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none" }}
    >
      <Box
        sx={{
          position: "relative",
          width: 34,
          height: 18,
          bgcolor: checked ? RED : "#b8b4aa",
          borderRadius: 9,
          flexShrink: 0,
          transition: "background 0.2s",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 2,
            left: checked ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: "50%",
            bgcolor: "#fff",
            transition: "left 0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          },
        }}
      />
      <Box sx={{ fontSize: 13, color: "#444", fontFamily: "Roboto, sans-serif" }}>{label}</Box>
    </Box>
  );
}

/* ── SERVINGS CONTROL ── */
function ServingsControl({ servings, onChange }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        component="button"
        onClick={() => onChange(Math.max(1, servings - 1))}
        disabled={servings <= 1}
        sx={{
          width: 26,
          height: 26,
          borderRadius: "6px",
          border: "1.5px solid",
          borderColor: servings <= 1 ? "#d4d0c8" : "#b8b4aa",
          bgcolor: "#faf9f7",
          color: servings <= 1 ? "#b8b4aa" : "#444",
          fontSize: 16,
          lineHeight: 1,
          cursor: servings <= 1 ? "default" : "pointer",
          fontFamily: "Roboto, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "&:hover": servings > 1 ? { borderColor: "#a01c27", color: "#a01c27" } : {},
        }}
      >
        −
      </Box>
      <Box sx={{ minWidth: 80, textAlign: "center" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#333", lineHeight: 1 }}>
          {servings} serving{servings !== 1 ? "s" : ""}
        </Typography>
      </Box>
      <Box
        component="button"
        onClick={() => onChange(servings + 1)}
        sx={{
          width: 26,
          height: 26,
          borderRadius: "6px",
          border: "1.5px solid #b8b4aa",
          bgcolor: "#faf9f7",
          color: "#444",
          fontSize: 16,
          lineHeight: 1,
          cursor: "pointer",
          fontFamily: "Roboto, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "&:hover": { borderColor: "#a01c27", color: "#a01c27" },
        }}
      >
        +
      </Box>
    </Box>
  );
}

ServingsControl.propTypes = {
  servings: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

/* ── PREP ITEM (left panel) ── */
function PrepItem({ label, checked, onToggle, isPantry }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.5,
        cursor: "pointer",
        userSelect: "none",
        opacity: isPantry ? 0.85 : 1,
      }}
    >
      <CheckCircleOutline
        sx={{
          fontSize: 16,
          color: checked ? GREEN : isPantry ? "#888" : "#b0aca4",
          flexShrink: 0,
          transition: "color 0.15s",
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontSize: 14,
          color: checked ? "#999" : isPantry ? "#555" : "#2a2a2a",
          textDecoration: checked ? "line-through" : "none",
          flex: 1,
          fontStyle: isPantry ? "italic" : "normal",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* ── STEP INGREDIENT CHIP ── */
function StepIng({ label, checked, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 20,
        fontSize: 13,
        cursor: "pointer",
        border: "1.5px solid",
        userSelect: "none",
        bgcolor: checked ? GREEN_LIGHT : "#eeece8",
        borderColor: checked ? GREEN : "#c8c4bc",
        color: checked ? GREEN : "#3a3a3a",
        textDecoration: checked ? "line-through" : "none",
        transition: "all 0.15s",
        "&:hover": { borderColor: checked ? GREEN : RED },
      }}
    >
      <CheckCircleOutline sx={{ fontSize: 13 }} />
      {label}
    </Box>
  );
}

/* ── RECORDING CARD ── */
function RecordingCard({
  audio,
  idx,
  isPlaying,
  progress,
  duration,
  recDone,
  stepChecked,
  isTextMode,
  onPlayPause,
  onTimeUpdate,
  onLoadedMetadata,
  onEnded,
  audioRef,
  onToggleText,
  onToggleIng,
  onToggleDone,
  stepLocked,
  onSeek,
  pantryItems,
  techniqueState,
  onDismissTechnique,
  expandedTechniqueIds,
  onToggleTechnique,
  previewOnly,
}) {
  const pct = Math.min(100, (progress || 0) * 100);

  // Rationale collapsing. The instruction (descriptionCollapsed, built server
  // side by prose_checks.remainder_after_span()) always renders as its own
  // paragraph; the "why" sentence renders as a separate line underneath so it
  // never hides inline where a skim reader would miss it. The span's own
  // technique decides whether that line starts folded: once the cook is
  // Comfortable with it — or has said "Got it" — it folds behind a "Why?" tap.
  const rationale = (audio.rationale || [])[0];
  const rationaleCollapsible = Boolean(audio.descriptionCollapsed && rationale);
  const [rationaleRevealed, setRationaleRevealed] = useState(false);
  const rationaleFolded =
    rationaleCollapsible &&
    !rationaleRevealed &&
    isCollapsed(techniqueState, rationale.technique_id);
  const stepProse = rationaleCollapsible ? audio.descriptionCollapsed : audio.description;

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        border: "2px solid",
        borderColor: recDone ? GREEN : isPlaying ? RED : "#c8ccd4",
        bgcolor: "#fff",
        boxShadow: isPlaying ? "0 4px 16px rgba(160,28,39,0.12)" : "0 1px 4px rgba(0,0,0,0.08)",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip
            title={
              stepLocked
                ? "Recipe already completed"
                : recDone
                ? "Mark step as not done"
                : "Mark step as done"
            }
          >
            <Box
              onClick={onToggleDone}
              role="checkbox"
              aria-checked={recDone}
              aria-disabled={stepLocked}
              aria-label={`Step ${idx + 1} done`}
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: recDone ? GREEN : RED,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                cursor: stepLocked ? "default" : "pointer",
                userSelect: "none",
                transition: "background 0.3s, transform 0.15s",
                "&:hover": { transform: stepLocked ? "none" : "scale(1.12)" },
              }}
            >
              {recDone ? "✓" : idx + 1}
            </Box>
          </Tooltip>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: 15,
              color: recDone ? "#888" : "inherit",
              textDecoration: recDone ? "line-through" : "none",
            }}
          >
            {audio.title.charAt(0).toUpperCase() + audio.title.slice(1)}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <Toggle checked={isTextMode} onChange={onToggleText} label="Text" />
        </Box>
      </Box>

      {/* Step ingredients + pantry items */}
      {(audio.ingredients?.length > 0 || pantryItems?.length > 0) && (
        <Box sx={{ px: 2.5, pb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "#7a7a7a",
              mb: 0.75,
            }}
          >
            This step
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {(audio.ingredients || []).map((ing, i) => (
              <StepIng
                key={i}
                label={ing}
                checked={!!stepChecked[i]}
                onToggle={() => onToggleIng(i)}
              />
            ))}
            {(pantryItems || []).map((item, i) => (
              <Box
                key={`pantry-${i}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1,
                  py: 0.5,
                  borderRadius: 20,
                  fontSize: 12,
                  border: "1.5px dashed #b0aca4",
                  color: "#666",
                  fontStyle: "italic",
                }}
              >
                {fmtPantryItem(item, false)}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Text mode content */}
      {isTextMode ? (
        <Box sx={{ px: 2.5, pb: 2, pt: 0.5, borderTop: "1px solid #e0ddd8" }}>
          <Typography variant="body2" sx={{ fontSize: 14, lineHeight: 1.8, color: "#2a2a2a" }}>
            {stepProse || "No description provided."}
          </Typography>

          {/* Nothing is ever deleted, only folded — one tap brings the
              sentence back, on its own line rather than merged back into the
              instruction paragraph. */}
          {rationaleCollapsible &&
            (rationaleFolded ? (
              <Button
                size="small"
                onClick={() => setRationaleRevealed(true)}
                sx={{
                  fontSize: 11,
                  textTransform: "none",
                  fontWeight: 600,
                  color: RED,
                  minWidth: 0,
                  px: 0,
                  mt: 0.5,
                }}
              >
                Why?
              </Button>
            ) : (
              <Box
                sx={{
                  mt: 0.75,
                  pl: 1.25,
                  borderLeft: "3px solid",
                  borderColor: RED_LIGHT,
                  fontSize: 12.5,
                  fontStyle: "italic",
                  lineHeight: 1.6,
                  color: MUTED,
                }}
              >
                {rationale.text}
              </Box>
            ))}

          <TechniqueChips
            techniques={audio.techniques}
            onDismiss={onDismissTechnique}
            expandedIds={expandedTechniqueIds}
            onToggleExpand={onToggleTechnique}
          />

          {/* Text mode has no audio to reach 90%, so this is the only way a
              step can be marked done — without it the step counter can never
              move. Hidden in preview: there is no cook in progress to track. */}
          {!previewOnly && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
              <Button
                size="small"
                variant={recDone ? "contained" : "outlined"}
                startIcon={recDone ? <CheckCircle /> : <CheckCircleOutline />}
                onClick={onToggleDone}
                disabled={stepLocked}
                sx={{
                  fontSize: 12,
                  textTransform: "none",
                  fontWeight: 600,
                  ...(recDone
                    ? {
                        bgcolor: GREEN,
                        color: "#fff",
                        "&:hover": { bgcolor: "#357a38", color: "#fff" },
                      }
                    : {
                        color: RED,
                        borderColor: RED,
                        "&:hover": { bgcolor: RED_LIGHT, borderColor: RED },
                      }),
                }}
              >
                {recDone ? "Step done" : "Mark step done"}
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            px: 2.5,
            pb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            opacity: audio.src ? 1 : 0.35,
            pointerEvents: audio.src ? "auto" : "none",
          }}
        >
          <IconButton
            onClick={() => onPlayPause(idx)}
            sx={{
              bgcolor: audio.src ? RED : "#ccc",
              color: "#fff",
              "&:hover": { bgcolor: audio.src ? "#7d1520" : "#ccc" },
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            {recDone ? <Replay /> : isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11, color: "#777", mb: 0.5 }}>
              {audio.src ? `${fmt(progress * duration)} / ${fmt(duration)}` : "0:00 / 0:00"}
            </Typography>
            <Box
              onClick={(e) => {
                if (!audio.src) return;
                const rect = e.currentTarget.getBoundingClientRect();
                onSeek((e.clientX - rect.left) / rect.width);
              }}
              sx={{
                height: 7,
                bgcolor: "#d8d4cc",
                borderRadius: 4,
                overflow: "hidden",
                cursor: audio.src ? "pointer" : "default",
              }}
            >
              <Box
                sx={{
                  width: `${pct}%`,
                  height: "100%",
                  bgcolor: recDone ? GREEN : "#8a8480",
                  borderRadius: 4,
                  transition: "width 0.2s linear",
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {!isTextMode && audio.src && (
        <audio
          ref={audioRef}
          src={audio.src}
          preload="metadata"
          onLoadedMetadata={() => onLoadedMetadata(idx)}
          onTimeUpdate={() => onTimeUpdate(idx)}
          onEnded={() => onEnded(idx)}
        />
      )}
    </Card>
  );
}

// Ingredient display lives in utils/ingredientFormatting — see the import at
// the top of this file. This component used to carry its own copy of every
// table and rule in there (unit labels, mass nouns, sub-units, fraction
// snapping), which is how the cooking view came to render "3 cup chicken
// broth" while the Groceries tab rendered "3 cups": two formatters drifting
// apart word by word. One formatter, every surface.

/* ── MAIN COMPONENT ── */

// Per-step progress is stored per program *and* recipe: the same recipe can sit
// in two schedules, and each week is its own cook.
function stepProgressKey(programId, recipeId) {
  if (!programId || !recipeId) return null;
  return `estia_step_progress_${programId}_${recipeId}`;
}

// Read back what the user had already ticked off. Anything that doesn't match
// the recipe as it is now (a different number of steps, unparseable JSON, a
// browser with storage disabled) is discarded rather than half-applied —
// restoring the wrong steps is worse than restoring none.
function readStepProgress(key, stepCount) {
  if (!key) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "null");
    if (!parsed || !Array.isArray(parsed.recDone) || parsed.recDone.length !== stepCount) {
      return null;
    }
    return {
      recDone: parsed.recDone.map(Boolean),
      stepChecked:
        parsed.stepChecked && typeof parsed.stepChecked === "object" ? parsed.stepChecked : {},
      prepChecked:
        parsed.prepChecked && typeof parsed.prepChecked === "object" ? parsed.prepChecked : {},
    };
  } catch {
    return null;
  }
}

function RecipeAudioList({
  recipeTitle,
  recipeXp,
  recipeTime,
  passiveWaitMinutes,
  mealType,
  diet,
  defaultServings,
  servings: servingsProp,
  onServingsChange,
  isCompleted,
  onMarkCompleted,
  onRewardDismissed,
  audios,
  allIngredients,
  pantryItems,
  programId,
  recipeId,
  initialSaved,
  initialRating,
  initialNote,
  hideHeader,
  onFormattedIngredients,
  previewOnly,
  headerSlot,
}) {
  const { metric, setMetric } = useUnitSystem();
  const baseServings = defaultServings || CATALOG_DEFAULT_SERVINGS;
  // Step progress survives a reload. It used to be component state and nothing
  // else, so a refresh mid-cook — a phone locking, a tab restoring, a wet hand
  // on the wrong control — put a half-cooked recipe back to "Recordings done
  // 0 / 3". A recipe the server already reports as completed shows every step
  // done, and outranks anything stored locally.
  const progressKey = previewOnly ? null : stepProgressKey(programId, recipeId);
  const restoredProgress = () =>
    isCompleted ? null : readStepProgress(progressKey, audios.length);
  // Controlled from parent when onServingsChange is provided; otherwise local state
  const [localServings, setLocalServings] = useState(servingsProp ?? baseServings);
  const servings = servingsProp ?? localServings;
  const setServings = (v) => {
    if (onServingsChange) onServingsChange(v);
    else setLocalServings(v);
  };
  const [pendingServings, setPendingServings] = useState(null);
  const [isSaved, setIsSaved] = useState(initialSaved ?? false);
  const [rating, setRating] = useState(initialRating ?? 0);
  const [note, setNote] = useState(initialNote ?? "");
  const [noteEditing, setNoteEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [ratingSaving, setRatingSaving] = useState(false);
  const [xpFlash, setXpFlash] = useState(false);
  const [flashXp, setFlashXp] = useState(recipeXp || 0);
  // Completion payload for the reward moment, and the persistent
  // "+90 recipe / +50 goal" breakdown that replaces the bare combined total.
  const [completionResult, setCompletionResult] = useState(null);
  const [xpBreakdown, setXpBreakdown] = useState(null);
  // Offered once the reward has been seen, never on top of it. The Cookbook is
  // a shelf of things the user liked, so the ask has to come after they know
  // how the cook went — and it must not interrupt it.
  const [cookbookOffer, setCookbookOffer] = useState(false);

  useEffect(() => {
    setLocalServings(servingsProp ?? baseServings);
    // Restore rather than clear: this fires on mount too, and clearing here is
    // what would undo the step progress read back below.
    const restored = restoredProgress();
    setStepChecked(restored?.stepChecked ?? {});
    setPrepChecked(restored?.prepChecked ?? {});
    setIsSaved(initialSaved ?? false);
    setRating(initialRating ?? 0);
    setNote(initialNote ?? "");
    setNoteEditing(false);
    setCompletionResult(null);
    setXpBreakdown(null);
    setCookbookOffer(false);
  }, [recipeId, initialSaved, initialRating, initialNote]);

  const handleSaveRecipe = async () => {
    if (saving || !recipeId) return;
    if (!isCompleted && !completionResult) {
      setSnackMsg("Cook this recipe first — your Cookbook holds dishes you've made.");
      return;
    }
    setSaving(true);
    try {
      // Idempotent both ways — see the retry note in utils/apiClient.
      if (isSaved) {
        await apiClient.delete(ENDPOINTS.collections.recipeSave(recipeId), { idempotent: true });
        setIsSaved(false);
        setSnackMsg("Removed from your Cookbook.");
      } else {
        await apiClient.post(ENDPOINTS.collections.recipeSave(recipeId), null, {
          idempotent: true,
        });
        setIsSaved(true);
        setCookbookOffer(false);
        setSnackMsg("Added to your Cookbook.");
      }
    } catch (err) {
      console.error("Save recipe failed:", err.message);
      setSnackMsg(
        err.response?.data?.detail ||
          "Couldn't reach your Cookbook — this recipe wasn't saved. Try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // The cooking view has now shown the reward moment, so drop the server-side
  // one-shot flags that would otherwise replay the same beat on /dashboard.
  const clearPendingCelebrations = async () => {
    try {
      await apiClient.delete(ENDPOINTS.users.clearCelebrations);
    } catch {
      /* non-critical — worst case the dashboard shows the moment again */
    }
  };

  const handleRatingClick = async (star) => {
    if (ratingSaving || !recipeId) return;
    if (!isCompleted) {
      setSnackMsg("Complete this recipe before rating it.");
      return;
    }
    setRatingSaving(true);
    try {
      await apiClient.post(ENDPOINTS.collections.rateRecipe(recipeId), {
        rating: star,
        note: note || null,
      });
      setRating(star);
      setNoteEditing(true);
    } catch (err) {
      console.error("Rate recipe failed:", err.message);
    } finally {
      setRatingSaving(false);
    }
  };

  const handleNoteSave = async () => {
    if (rating === 0 || !recipeId) return;
    try {
      await apiClient.post(ENDPOINTS.collections.rateRecipe(recipeId), {
        rating,
        note: note || null,
      });
      setNoteEditing(false);
    } catch (err) {
      console.error("Save note failed:", err.message);
    }
  };

  // ── Technique layer ──
  // One fetch for the whole recipe rather than one per step: every card needs
  // the same tier/dismissal map to decide what to collapse.
  const [techniqueState, setTechniqueState] = useState({});
  // Chips the cook has toggled by hand this session. Held as an override on top
  // of the default, so tapping a collapsed chip opens it and tapping an open
  // one closes it, without either being written back as a preference.
  const [expandedTechniqueIds, setExpandedTechniqueIds] = useState(() => new Set());

  useEffect(() => {
    apiClient
      .get(ENDPOINTS.techniques.me)
      .then((res) => {
        const next = {};
        (res.data?.techniques || []).forEach((t) => {
          next[t.id] = { tier: t.tier, dismissed: t.dismissed };
        });
        setTechniqueState(next);
      })
      // Fail open: an unreachable profile means every explanation stays
      // expanded, which is the new-account default anyway.
      .catch(() => setTechniqueState({}));
  }, []);

  const toggleTechniqueExpanded = (techniqueId) => {
    setExpandedTechniqueIds((prev) => {
      const next = new Set(prev);
      if (next.has(techniqueId)) next.delete(techniqueId);
      else next.add(techniqueId);
      return next;
    });
  };

  const dismissTechnique = async (techniqueId) => {
    // Optimistic: "Got it" should collapse the chip on the tap, not after a
    // round trip, and the worst case of a failed write is that it comes back
    // next time.
    setTechniqueState((prev) => ({
      ...prev,
      [techniqueId]: { ...(prev[techniqueId] || {}), dismissed: true },
    }));
    setExpandedTechniqueIds((prev) => {
      const next = new Set(prev);
      next.delete(techniqueId);
      return next;
    });
    try {
      await apiClient.post(ENDPOINTS.techniques.dismiss(techniqueId));
    } catch (err) {
      console.error("Dismiss technique failed:", err.message);
    }
  };

  const [currentIndex, setCurrentIndex] = useState(null);
  const [progresses, setProgresses] = useState(() => Array(audios.length).fill(0));
  const [durations, setDurations] = useState(() => Array(audios.length).fill(0));
  const [autoplay, setAutoplay] = useState(false);
  const [textModes, setTextModes] = useState(() => {
    const modes = {};
    audios.forEach((_, i) => {
      modes[i] = true;
    });
    return modes;
  });
  const [recDone, setRecDone] = useState(
    () => restoredProgress()?.recDone ?? Array(audios.length).fill(!!isCompleted)
  );
  const [stepChecked, setStepChecked] = useState(() => restoredProgress()?.stepChecked ?? {});
  const [prepChecked, setPrepChecked] = useState(() => restoredProgress()?.prepChecked ?? {});
  const autoCompletedRef = useRef(false);
  const audioRefs = useRef([]);

  useEffect(() => {
    const restored = restoredProgress();
    setProgresses(Array(audios.length).fill(0));
    setDurations(Array(audios.length).fill(0));
    setRecDone(restored?.recDone ?? Array(audios.length).fill(!!isCompleted));
    setStepChecked(restored?.stepChecked ?? {});
    setTextModes(() => {
      const modes = {};
      audios.forEach((_, i) => {
        modes[i] = true;
      });
      return modes;
    });
    setCurrentIndex(null);
    autoCompletedRef.current = false;
    audioRefs.current = audioRefs.current.slice(0, audios.length);
  }, [audios]);

  // Marking the whole recipe complete implies every step is done.
  useEffect(() => {
    if (isCompleted) {
      setRecDone((prev) => (prev.every(Boolean) ? prev : prev.map(() => true)));
    }
  }, [isCompleted]);

  // Write progress back on every change. Once the recipe is completed the
  // server owns that fact, so the local copy is dropped rather than left to
  // shadow it — a stale blob would restore ticks onto a re-cook of the same
  // recipe in a later week.
  useEffect(() => {
    if (!progressKey) return;
    try {
      if (isCompleted) {
        localStorage.removeItem(progressKey);
      } else if (recDone.some(Boolean) || Object.keys(prepChecked).length > 0) {
        localStorage.setItem(progressKey, JSON.stringify({ recDone, stepChecked, prepChecked }));
      } else {
        localStorage.removeItem(progressKey);
      }
    } catch {
      /* storage full or disabled — progress just won't survive a reload */
    }
  }, [progressKey, isCompleted, recDone, stepChecked, prepChecked]);

  const toggleRecDone = (idx) => {
    // Steps are locked once the recipe is complete — otherwise un-checking one
    // would show "2 / 3" on a recipe already marked Completed.
    if (isCompleted) return;
    setRecDone((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const triggerComplete = async () => {
    const result = await onMarkCompleted();
    setCompletionResult(result && !result.failed ? result : null);
    setXpBreakdown(
      result && !result.failed
        ? {
            recipeXp: result.xp_gained ?? 0,
            goalXp: result.goal_xp ?? 0,
            totalXp: result.total_xp ?? 0,
            goals: (result.goals_updated || []).filter((g) => g.completed),
            techniques: result.techniques_practiced || [],
          }
        : null
    );
    setFlashXp(result?.total_xp ?? recipeXp ?? 0);
    setXpFlash(true);
    setTimeout(() => setXpFlash(false), 2200);

    // The reward moment has fired here — drop the server-side one-shot flags so
    // /dashboard doesn't replay it.
    if (result && !result.failed) clearPendingCelebrations();
  };

  // The reward has been seen. Only now does anything else get to ask for the
  // user's attention: the week moment if the week just ended, and the Cookbook
  // offer if this is a recipe they might want to keep.
  const dismissReward = () => {
    const finishedWeek = !!completionResult?.schedule_completed;
    setCompletionResult(null);
    if (finishedWeek) {
      if (onRewardDismissed) onRewardDismissed();
    } else if (!isSaved) {
      setCookbookOffer(true);
    }
  };

  // Auto-complete when every step is done — via audio reaching 90% or the
  // user marking steps done by hand in text mode.
  useEffect(() => {
    if (!isCompleted && !autoCompletedRef.current && recDone.length > 0 && recDone.every(Boolean)) {
      autoCompletedRef.current = true;
      triggerComplete();
    }
  }, [recDone, isCompleted, onMarkCompleted]);

  // Play/pause audio elements
  useEffect(() => {
    const cur = audioRefs.current[currentIndex];
    if (cur) cur.play().catch(() => {});
    audioRefs.current.forEach((a, i) => {
      if (i !== currentIndex && a) a.pause();
    });
  }, [currentIndex]);

  const handlePlayPause = (idx) => {
    setCurrentIndex((prev) => (prev === idx ? null : idx));
  };

  const handleTimeUpdate = (idx) => {
    const audio = audioRefs.current[idx];
    if (!audio?.duration) return;
    const progress = audio.currentTime / audio.duration;
    setProgresses((prev) => {
      const c = [...prev];
      c[idx] = progress;
      return c;
    });

    // Auto-check step ingredients at 90%
    if (progress >= 0.9) {
      setRecDone((prev) => {
        if (prev[idx]) return prev;
        const c = [...prev];
        c[idx] = true;
        return c;
      });
      setStepChecked((prev) => {
        if (prev[idx]) return prev;
        const ingCount = audios[idx]?.ingredients?.length || 0;
        const checked = {};
        for (let i = 0; i < ingCount; i++) checked[i] = true;
        return { ...prev, [idx]: checked };
      });
    }
  };

  const handleLoadedMetadata = (idx) => {
    const audio = audioRefs.current[idx];
    if (audio?.duration) {
      setDurations((prev) => {
        const c = [...prev];
        c[idx] = audio.duration;
        return c;
      });
    }
  };

  const handleEnded = (idx) => {
    if (autoplay) setCurrentIndex((idx + 1) % audios.length);
    else setCurrentIndex(null);
  };

  const handleSeek = (idx, pct) => {
    const audio = audioRefs.current[idx];
    if (audio?.duration) audio.currentTime = pct * audio.duration;
    setProgresses((prev) => {
      const c = [...prev];
      c[idx] = pct;
      return c;
    });
  };

  const toggleIng = (recIdx, ingIdx) => {
    const label = fmtedAudios[recIdx]?.ingredients?.[ingIdx];
    const newVal = !stepChecked[recIdx]?.[ingIdx];

    setStepChecked((prev) => {
      const next = { ...prev };
      fmtedAudios.forEach((audio, rIdx) => {
        (audio.ingredients || []).forEach((ing, iIdx) => {
          if (ing === label) {
            next[rIdx] = { ...(next[rIdx] || {}), [iIdx]: newVal };
          }
        });
      });
      return next;
    });

    setPrepChecked((prev) => {
      if (!label) return prev;
      const next = { ...prev };
      fmtedAllIngredients.forEach((ing, i) => {
        if (ing === label) next[`ing-${i}`] = newVal;
      });
      return next;
    });
  };

  const toggleText = (idx) => setTextModes((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const allTextMode = audios.length > 0 && audios.every((_, i) => textModes[i]);
  const toggleAllText = () => {
    const next = !allTextMode;
    const modes = {};
    audios.forEach((_, i) => {
      modes[i] = next;
    });
    setTextModes(modes);
  };

  const doneCount = recDone.filter(Boolean).length;
  const progressPct = audios.length > 0 ? (doneCount / audios.length) * 100 : 0;

  // Format ingredients for display (metric-aware, serving-scaled)
  const scale = servings / baseServings;
  const fmtedAudios = audios.map((audio) => ({
    ...audio,
    ingredients: (audio.rawIngredients || audio.ingredients || [])
      .map((ing) => (typeof ing === "string" ? ing : fmtIng(scaleIng(ing, scale), metric)))
      .filter(Boolean),
  }));
  const fmtedAllIngredients = (allIngredients || [])
    .map((ing) => (typeof ing === "string" ? ing : fmtIng(scaleIng(ing, scale), metric)))
    .filter(Boolean);

  useEffect(() => {
    if (onFormattedIngredients) onFormattedIngredients(fmtedAllIngredients, servings);
  }, [fmtedAllIngredients.join(","), servings]);

  // Derive which "Before you start" ingredients are fully used across all steps
  const stepDoneLabels = (() => {
    const labelAppearances = {};
    const labelChecked = {};
    fmtedAudios.forEach((audio, recIdx) => {
      (audio.ingredients || []).forEach((label, ingIdx) => {
        labelAppearances[label] = (labelAppearances[label] || 0) + 1;
        if (stepChecked[recIdx]?.[ingIdx]) {
          labelChecked[label] = (labelChecked[label] || 0) + 1;
        }
      });
    });
    return new Set(
      Object.keys(labelAppearances).filter(
        (label) => labelChecked[label] === labelAppearances[label]
      )
    );
  })();

  return (
    <Grid container spacing={3} alignItems="flex-start">
      {/* ── LEFT PANEL ── */}
      {!hideHeader && (
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: "sticky" }, top: { md: "110px" } }}>
            <Card sx={{ p: 2.5, borderRadius: 2, bgcolor: "#fff", border: "1px solid #c8ccd4" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "#222", fontSize: "1.2rem", flex: 1, pr: 0.5 }}
                >
                  {recipeTitle}
                </Typography>
              </Box>

              {/* Anything the surrounding screen wants to sit with the recipe
                  rather than above it — the cooking view's repair controls,
                  which carry the "your week locks" line. It went here because
                  that line was the first thing on the screen, read before the
                  user had seen which recipe they were on. */}
              {headerSlot}

              {/* The one persistent save control for a recipe. It used to be a
                  bare bookmark icon competing with a header "Save" that kept
                  the whole schedule and a modal that kept the recipe — three
                  affordances, and testers could not tell which had fired. This
                  one says Cookbook, and says whether the recipe is already in
                  there. */}
              {!previewOnly && (
                <Tooltip
                  describeChild
                  title={
                    isSaved
                      ? "In your Cookbook — click to remove"
                      : "Your Cookbook is the shelf of recipes you'd make again"
                  }
                >
                  <Box
                    component="button"
                    onClick={handleSaveRecipe}
                    disabled={saving}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.75,
                      width: "100%",
                      mb: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: "Roboto, sans-serif",
                      cursor: "pointer",
                      border: "1px solid",
                      ...(isSaved
                        ? { bgcolor: GREEN_LIGHT, color: "#2e7d32", borderColor: GREEN }
                        : {
                            bgcolor: "#fff",
                            color: RED,
                            borderColor: RED,
                            "&:hover": { bgcolor: RED_LIGHT },
                          }),
                    }}
                  >
                    {isSaved ? (
                      <Bookmark sx={{ fontSize: 15, color: "#2e7d32" }} />
                    ) : (
                      <BookmarkBorder sx={{ fontSize: 15, color: RED }} />
                    )}
                    {isSaved ? "In your Cookbook" : "Add to Cookbook"}
                  </Box>
                </Tooltip>
              )}

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                <Chip
                  icon={<AccessTime sx={{ fontSize: "14px !important" }} />}
                  label={`${recipeTime} min`}
                  size="small"
                  sx={{ bgcolor: "#e0ddd8", color: "#444" }}
                />
                <Chip
                  icon={<Star sx={{ fontSize: "14px !important" }} />}
                  label={`${recipeXp} XP`}
                  size="small"
                  sx={{ bgcolor: GREEN_LIGHT, color: "#2e7d32" }}
                />
                {mealType && MEAL_TYPE_LABELS[mealType] && (
                  <Chip
                    label={MEAL_TYPE_LABELS[mealType].label}
                    size="small"
                    sx={{
                      bgcolor: MEAL_TYPE_LABELS[mealType].bg,
                      color: MEAL_TYPE_LABELS[mealType].color,
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  />
                )}
                {/* The diet label the filter actually reads. Without it the
                    filter is an unverifiable promise: a user cannot check
                    whether a dish matches what they told us, and a mislabelled
                    recipe has no surface on which to be caught. */}
                {recipeDietLabel(diet) && (
                  <Chip
                    label={recipeDietLabel(diet)}
                    size="small"
                    sx={{
                      bgcolor: RECIPE_DIET_STYLE[diet]?.bg || "#e0ddd8",
                      color: RECIPE_DIET_STYLE[diet]?.color || "#444",
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  />
                )}
              </Box>

              {/* Passive wait — a planning warning, not a category chip, so it
                  gets its own line rather than folding into the chip row
                  above where it could be misread as more active prep time. */}
              {passiveWaitLabel(passiveWaitMinutes) && (
                <Box
                  sx={{
                    display: "inline-block",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#8a4b00",
                    bgcolor: "#fff3e0",
                    border: "1px solid #f5d9a8",
                    borderRadius: "6px",
                    px: 1,
                    py: 0.5,
                    mb: 1.5,
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  {passiveWaitLabel(passiveWaitMinutes)} — plan to start this ahead of when you want
                  to eat it
                </Box>
              )}

              {/* "● Active" means a cook is under way. Nothing is under way
                  when you are reading a recipe in the browse catalog. */}
              {!previewOnly && (
                <Chip
                  label={isCompleted ? "✓ Completed" : "● Active"}
                  size="small"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    fontSize: 11,
                    bgcolor: isCompleted ? GREEN_LIGHT : RED_LIGHT,
                    color: isCompleted ? GREEN : RED,
                  }}
                />
              )}

              {/* Servings scaler */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid #dedad4",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: "#6e6e6e",
                  }}
                >
                  Servings
                </Typography>
                <ServingsControl servings={servings} onChange={setPendingServings} />
              </Box>

              {/* Recording progress. Always 0/N in preview — a progress bar
                  that cannot move is noise. */}
              {!previewOnly && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Typography variant="caption" color="secondary">
                      Recordings done
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {doneCount} / {audios.length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mb: 2.5,
                      height: 6,
                      bgcolor: "#d8d4cc",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${progressPct}%`,
                        height: "100%",
                        bgcolor: RED,
                        borderRadius: 3,
                        transition: "width 0.4s",
                      }}
                    />
                  </Box>
                </>
              )}

              {/* Star rating. Hidden in preview for two reasons: rating a dish
                  you haven't cooked isn't a judgement worth storing, and the
                  browse catalog must show no rating anywhere — its ordering
                  leans on ratings and displaying one next to that ordering
                  invites reading the list as a leaderboard. */}
              {!previewOnly && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      color: "#6e6e6e",
                      mb: 0.5,
                    }}
                  >
                    Rate this recipe
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconButton
                        key={star}
                        onClick={() => handleRatingClick(star)}
                        size="small"
                        sx={{
                          p: 0.25,
                          color: star <= rating ? "#f5a623" : "#c0b090",
                          "&:hover": { color: "#f5a623" },
                          transition: "color 0.15s",
                        }}
                      >
                        <Star sx={{ fontSize: 22 }} />
                      </IconButton>
                    ))}
                  </Box>
                  {rating > 0 &&
                    (noteEditing ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Add a note (optional)…"
                          multiline
                          maxRows={3}
                          size="small"
                          fullWidth
                          autoFocus
                          sx={{ "& .MuiOutlinedInput-root": { fontSize: 12 } }}
                        />
                        <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                          <Box
                            component="button"
                            onClick={handleNoteSave}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 20,
                              fontSize: 11,
                              fontWeight: 600,
                              bgcolor: RED,
                              color: "#fff",
                              border: "none",
                              cursor: "pointer",
                              fontFamily: "Roboto, sans-serif",
                              "&:hover": { bgcolor: "#7d1520" },
                            }}
                          >
                            Save
                          </Box>
                          <Box
                            component="button"
                            onClick={() => {
                              setNote(initialNote ?? "");
                              setNoteEditing(false);
                            }}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 20,
                              fontSize: 11,
                              fontWeight: 500,
                              bgcolor: "transparent",
                              color: "#888",
                              border: "1px solid #ddd",
                              cursor: "pointer",
                              fontFamily: "Roboto, sans-serif",
                              "&:hover": { color: "#444" },
                            }}
                          >
                            Cancel
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 1, display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                        <Box
                          sx={{
                            flex: 1,
                            fontSize: 12,
                            color: note ? "#2a2a2a" : "#888",
                            fontFamily: "Roboto, sans-serif",
                            lineHeight: 1.5,
                            fontStyle: note ? "normal" : "italic",
                          }}
                        >
                          {note || "No note yet"}
                        </Box>
                        <Box
                          component="button"
                          onClick={() => setNoteEditing(true)}
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 10,
                            fontSize: 10,
                            fontWeight: 500,
                            bgcolor: "transparent",
                            color: RED,
                            border: `1px solid ${RED}`,
                            cursor: "pointer",
                            fontFamily: "Roboto, sans-serif",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            "&:hover": { bgcolor: "#fde8ea" },
                          }}
                        >
                          {note ? "Edit" : "Add note"}
                        </Box>
                      </Box>
                    ))}
                </Box>
              )}

              {/* Prep list */}
              <Box sx={{ borderTop: "1px solid #dedad4", pt: 2 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: "#6e6e6e",
                    mb: 1.25,
                  }}
                >
                  Before you start
                </Typography>
                {fmtedAllIngredients.map((ing, i) => (
                  <PrepItem
                    key={`ing-${i}`}
                    label={ing}
                    checked={!!prepChecked[`ing-${i}`] || stepDoneLabels.has(ing)}
                    onToggle={() => {
                      const newVal = !(prepChecked[`ing-${i}`] || stepDoneLabels.has(ing));
                      setPrepChecked((prev) => ({ ...prev, [`ing-${i}`]: newVal }));
                      setStepChecked((prev) => {
                        const next = { ...prev };
                        fmtedAudios.forEach((audio, rIdx) => {
                          (audio.ingredients || []).forEach((stepIng, iIdx) => {
                            if (stepIng === ing) {
                              next[rIdx] = { ...(next[rIdx] || {}), [iIdx]: newVal };
                            }
                          });
                        });
                        return next;
                      });
                    }}
                    isPantry={false}
                  />
                ))}
                {(pantryItems || []).length > 0 && (
                  <Box sx={{ borderTop: "1px solid #dedad4", mt: 0.5, pt: 0.5 }}>
                    {pantryItems.map((item, i) => (
                      <PrepItem
                        key={`pantry-${i}`}
                        label={fmtPantryItem(item, metric)}
                        checked={!!prepChecked[`pantry-${i}`]}
                        onToggle={() =>
                          setPrepChecked((prev) => ({
                            ...prev,
                            [`pantry-${i}`]: !prev[`pantry-${i}`],
                          }))
                        }
                        isPantry={true}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </Grid>
      )}

      {/* ── RIGHT: RECORDINGS ── */}
      <Grid item xs={12} md={hideHeader ? 12 : 8}>
        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2,
            py: 1.25,
            bgcolor: "#fff",
            border: "1px solid #c8ccd4",
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Toggle checked={autoplay} onChange={() => setAutoplay((v) => !v)} label="Autoplay" />
            <Toggle checked={allTextMode} onChange={toggleAllText} label="Text mode (all)" />
          </Box>
          <UnitToggle metric={metric} onChange={() => setMetric((v) => !v)} />
        </Box>

        {!allTextMode && !audios.some((a) => !!a.src) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              mb: 2,
              bgcolor: "#fff8f0",
              border: "2px solid #f5c080",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(200,120,0,0.1)",
            }}
          >
            <PlayArrow
              sx={{
                fontSize: 32,
                color: "#e07800",
                opacity: 0.85,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#b85c00" }}>
                Audio lessons coming soon
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#c8780a", mt: 0.25 }}>
                Switch to text mode above to follow the recipe steps
              </Typography>
            </Box>
          </Box>
        )}

        {fmtedAudios.map((audio, idx) => (
          <RecordingCard
            key={audio.id}
            audio={audio}
            idx={idx}
            isPlaying={currentIndex === idx}
            progress={progresses[idx]}
            duration={durations[idx]}
            recDone={recDone[idx]}
            stepChecked={stepChecked[idx] || {}}
            isTextMode={!!textModes[idx]}
            onPlayPause={handlePlayPause}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
            audioRef={(el) => (audioRefs.current[idx] = el)}
            onToggleText={() => toggleText(idx)}
            onToggleIng={(ingIdx) => toggleIng(idx, ingIdx)}
            onToggleDone={() => toggleRecDone(idx)}
            stepLocked={isCompleted}
            onSeek={(pct) => handleSeek(idx, pct)}
            pantryItems={audio.pantryItems || []}
            techniqueState={techniqueState}
            onDismissTechnique={dismissTechnique}
            expandedTechniqueIds={expandedTechniqueIds}
            onToggleTechnique={toggleTechniqueExpanded}
            previewOnly={previewOnly}
          />
        ))}

        {/* Completion. Suppressed entirely in preview mode: the browse catalog
            shows recipes the user has not started, and the completed variant of
            this control ("✓ Completed", disabled) would tell them they had
            already cooked something they are only reading about. */}
        {!previewOnly && (
          <Box
            sx={{
              mt: 2.5,
              pt: 2,
              borderTop: "1px solid #d8d4cc",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
              position: "relative",
            }}
          >
            {/* No caption here. "Mark every step done and the recipe
                completes itself" sat directly beside the MARK AS COMPLETE
                button it was describing. */}
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              {/* +XP flash badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: -36,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: GREEN,
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 15,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 20,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  fontFamily: "Roboto, sans-serif",
                  boxShadow: "0 2px 8px rgba(67,160,71,0.35)",
                  opacity: xpFlash ? 1 : 0,
                  transition: xpFlash ? "opacity 0.2s ease-in" : "opacity 0.6s ease-out",
                }}
              >
                +{flashXp || 0} XP
              </Box>
              <Button
                variant={isCompleted ? "contained" : "outlined"}
                disabled={isCompleted}
                startIcon={isCompleted ? <DoneAll /> : <CheckCircle />}
                onClick={triggerComplete}
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  ...(isCompleted
                    ? {
                        bgcolor: RED,
                        color: "#fff",
                        "&.Mui-disabled": { bgcolor: RED, color: "#fff", opacity: 1 },
                      }
                    : {
                        color: RED,
                        borderColor: RED,
                        "&:hover": { bgcolor: RED_LIGHT, borderColor: RED },
                      }),
                }}
              >
                {isCompleted ? "Completed" : "Mark as Complete"}
              </Button>
            </Box>
          </Box>
        )}

        {/* XP breakdown — a combined "+140 XP" alone left testers unable to
            tell what came from the recipe and what came from a goal. */}
        {xpBreakdown && (
          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              bgcolor: GREEN_LIGHT,
              border: `1px solid ${GREEN}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2e7d32" }}>
                Recipe complete
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#2e7d32" }}>
                +{xpBreakdown.totalXp} XP
              </Typography>
            </Box>
            <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 12, color: "#3a3a3a" }}>{recipeTitle}</Typography>
                <Typography sx={{ fontSize: 12, color: "#3a3a3a", fontWeight: 600 }}>
                  +{xpBreakdown.recipeXp} XP
                </Typography>
              </Box>
              {xpBreakdown.goals.map((g) => (
                <Box key={g.goal_id} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 12, color: "#3a3a3a" }}>Goal · {g.title}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#3a3a3a", fontWeight: 600 }}>
                    +{g.xp_awarded} XP
                  </Typography>
                </Box>
              ))}
              {xpBreakdown.goals.length === 0 && xpBreakdown.goalXp > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 12, color: "#3a3a3a" }}>Goals</Typography>
                  <Typography sx={{ fontSize: 12, color: "#3a3a3a", fontWeight: 600 }}>
                    +{xpBreakdown.goalXp} XP
                  </Typography>
                </Box>
              )}
              {/* The receipt keeps what the moment showed, reps included — the
                  moment is dismissible, this is the record of it. */}
              {xpBreakdown.techniques.map((t) => (
                <Box key={t.id} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 12, color: "#3a3a3a" }}>
                    Skill · {t.display_name}
                    {t.tier_up ? ` — now ${t.tier}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#3a3a3a", fontWeight: 600 }}>
                    +{t.reps_gained} rep{t.reps_gained === 1 ? "" : "s"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Typography
          variant="caption"
          sx={{ display: "block", color: MUTED, fontSize: 11, px: 2, pb: 2, mt: 1 }}
        >
          For general guidance only. Always use your own judgment regarding allergies, dietary
          needs, and food safety.
        </Typography>
      </Grid>
      <Snackbar
        open={!!snackMsg}
        autoHideDuration={3000}
        onClose={() => setSnackMsg("")}
        message={snackMsg}
      />

      <RewardMoment
        result={completionResult}
        recipeTitle={recipeTitle}
        hasWeekMoment={!!completionResult?.schedule_completed}
        onClose={dismissReward}
      />

      {/* The Cookbook ask, after the reward and out of its way. A snackbar, not
          a dialog: saving is worth offering, it is not worth taking the screen
          the reward just used. */}
      <Snackbar
        open={cookbookOffer && !isSaved}
        autoHideDuration={9000}
        onClose={() => setCookbookOffer(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "#fff",
            border: `1px solid ${BORDER}`,
            borderLeft: `4px solid ${RED}`,
            borderRadius: 2,
            px: 2,
            py: 1.25,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#222" }}>
            Would you cook <strong>{recipeTitle}</strong> again?
          </Typography>
          <Button
            onClick={handleSaveRecipe}
            disabled={saving}
            sx={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              color: RED,
              border: `1px solid ${RED}`,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: RED_LIGHT },
            }}
          >
            Add to Cookbook
          </Button>
        </Box>
      </Snackbar>

      {/* Servings confirmation dialog */}
      <Dialog
        open={pendingServings !== null}
        onClose={() => setPendingServings(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 0.5 }}>Change servings?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#2a2a2a", lineHeight: 1.6 }}>
            Your grocery list is set for{" "}
            <strong>
              {servings} serving{servings !== 1 ? "s" : ""}
            </strong>{" "}
            on this recipe. Changing to{" "}
            <strong>
              {pendingServings} serving{pendingServings !== 1 ? "s" : ""}
            </strong>{" "}
            will update the ingredient quantities shown here — make sure you have enough supplies.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setPendingServings(null)}
            sx={{ fontSize: 13, color: "#888", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setServings(pendingServings);
              setStepChecked({});
              setPrepChecked({});
              setPendingServings(null);
            }}
            sx={{
              fontSize: 13,
              textTransform: "none",
              bgcolor: RED,
              color: "#fff",
              "&:hover": { bgcolor: "#7d1520", color: "#fff" },
            }}
          >
            Yes, update servings
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

/* ── PROP TYPES ── */
PrepItem.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isPantry: PropTypes.bool,
};

StepIng.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

RecordingCard.propTypes = {
  audio: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    src: PropTypes.string,
    description: PropTypes.string,
    descriptionCollapsed: PropTypes.string,
    techniques: PropTypes.array,
    rationale: PropTypes.array,
    ingredients: PropTypes.arrayOf(PropTypes.string),
    pantryItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  }).isRequired,
  pantryItems: PropTypes.arrayOf(PropTypes.string),
  idx: PropTypes.number.isRequired,
  isPlaying: PropTypes.bool.isRequired,
  progress: PropTypes.number,
  duration: PropTypes.number,
  recDone: PropTypes.bool.isRequired,
  stepChecked: PropTypes.object.isRequired,
  isTextMode: PropTypes.bool.isRequired,
  onPlayPause: PropTypes.func.isRequired,
  onTimeUpdate: PropTypes.func.isRequired,
  onLoadedMetadata: PropTypes.func.isRequired,
  onEnded: PropTypes.func.isRequired,
  audioRef: PropTypes.func.isRequired,
  onToggleText: PropTypes.func.isRequired,
  onToggleIng: PropTypes.func.isRequired,
  onToggleDone: PropTypes.func.isRequired,
  stepLocked: PropTypes.bool,
  onSeek: PropTypes.func.isRequired,
  techniqueState: PropTypes.object,
  onDismissTechnique: PropTypes.func.isRequired,
  expandedTechniqueIds: PropTypes.instanceOf(Set),
  onToggleTechnique: PropTypes.func.isRequired,
  previewOnly: PropTypes.bool,
};

RecipeAudioList.propTypes = {
  recipeTitle: PropTypes.string.isRequired,
  recipeXp: PropTypes.number,
  recipeTime: PropTypes.number,
  passiveWaitMinutes: PropTypes.number,
  mealType: PropTypes.string,
  // The recipe's own label: "vegan" | "vegetarian" | "meat", or absent.
  diet: PropTypes.string,
  defaultServings: PropTypes.number,
  servings: PropTypes.number,
  onServingsChange: PropTypes.func,
  isCompleted: PropTypes.bool.isRequired,
  onMarkCompleted: PropTypes.func.isRequired,
  // Fired once the reward moment has been seen and closed. The week
  // celebration waits on this so the two never stack.
  onRewardDismissed: PropTypes.func,
  audios: PropTypes.array.isRequired,
  allIngredients: PropTypes.array.isRequired,
  pantryItems: PropTypes.arrayOf(PropTypes.string),
  // Step progress is stored per program + recipe. Without a program there is
  // no cook session to remember, so previews and standalone recipe views
  // simply don't persist.
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  recipeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialSaved: PropTypes.bool,
  initialRating: PropTypes.number,
  initialNote: PropTypes.string,
  hideHeader: PropTypes.bool,
  onFormattedIngredients: PropTypes.func,
  // Read-only preview (browse catalog): renders steps and ingredients but no
  // completion control, because the recipe isn't in a schedule to complete.
  previewOnly: PropTypes.bool,
  // Rendered in the header card under the title. See the comment at the call
  // site — this is where the cooking view's repair controls live.
  headerSlot: PropTypes.node,
};

export default RecipeAudioList;
